import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { color, radius, shadow } from "../../styles/theme";
import { SUPPORTED_LANGUAGES } from "../../i18n";

/**
 * EN/DE language selector — sits in the Navbar. Persists the choice via
 * i18next-browser-languagedetector (localStorage key "psPromoterLang").
 */
const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = i18n.language?.slice(0, 2) || "en";

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const activeLang = SUPPORTED_LANGUAGES.find((l) => l.code === current) || SUPPORTED_LANGUAGES[0];

  return (
    <div ref={ref} style={s.wrap}>
      <button
        type="button"
        style={s.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-label={t("languageSwitcher.label")}
        title={t("languageSwitcher.label")}
      >
        <Globe size={15} strokeWidth={2} />
        <span style={s.code}>{activeLang.short}</span>
      </button>

      {open && (
        <div className="modal-pop" style={s.dropdown}>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              className="row-link"
              style={s.item}
              onClick={() => {
                i18n.changeLanguage(lang.code);
                setOpen(false);
              }}
            >
              <span>{lang.label}</span>
              {current === lang.code && <Check size={14} strokeWidth={2.5} color={color.primary500} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const s = {
  wrap: { position: "relative" },
  trigger: {
    display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.09)", borderRadius: radius.sm, padding: "6px 10px",
    cursor: "pointer", color: "#d1d5db", fontSize: 12.5, fontWeight: 700, transition: "background 150ms",
  },
  code: { letterSpacing: 0.3 },
  dropdown: {
    position: "absolute", top: "calc(100% + 8px)", right: 0, width: 160,
    background: color.surface, borderRadius: radius.md, boxShadow: shadow.lg,
    border: `1px solid ${color.border}`, overflow: "hidden", zIndex: 200, padding: 4,
  },
  item: {
    width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
    padding: "9px 10px", background: "transparent", border: "none", fontSize: 13, color: color.gray700,
    fontWeight: 500, cursor: "pointer", textAlign: "left", borderRadius: radius.sm,
  },
};

export default LanguageSwitcher;
